import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Snackbar,
  Alert,
  CircularProgress,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
  FormHelperText
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  ViewList as ViewListIcon
} from '@mui/icons-material';
import { getCategories, createCategory, updateCategory, deleteCategory } from '../../services/categoryService';
import { Category, CategoryFormData } from '../../types/category';
import { ITax } from '../../types/tax';
import taxService from '../../services/taxService';

const CategoryList: React.FC = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [taxes, setTaxes] = useState<ITax[]>([]);
  const [loading, setLoading] = useState(true);
  const [taxesLoading, setTaxesLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [formData, setFormData] = useState<CategoryFormData>({
    name: '',
    description: '',
    replacementPeriod: 0,
    returnPeriod: 0,
    parentCategory: null,
    taxes: []
  });
  const [categoryType, setCategoryType] = useState<'main' | 'sub'>('main');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });
  
  // إضافة حالة للفلتر
  const [filters, setFilters] = useState({
    searchTerm: '',
    categoryTypeFilter: 'all' as 'all' | 'main' | 'sub',
    minReplacementPeriod: '',
    maxReplacementPeriod: '',
    minReturnPeriod: '',
    maxReturnPeriod: ''
  });
  
  // حالة لعرض/إخفاء قسم الفلتر
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchCategories();
    fetchTaxes();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const data = await getCategories();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setSnackbar({
        open: true,
        message: 'فشل في استرجاع التصنيفات',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const fetchTaxes = async () => {
    try {
      setTaxesLoading(true);
      const data = await taxService.getAllTaxes();
      setTaxes(data);
    } catch (error) {
      console.error('Error fetching taxes:', error);
      setSnackbar({
        open: true,
        message: 'فشل في استرجاع الضرائب',
        severity: 'error'
      });
    } finally {
      setTaxesLoading(false);
    }
  };

  const handleOpenDialog = (category?: Category) => {
    if (category) {
      setEditMode(true);
      setEditId(category._id);
      const hasParent = category.parentCategory !== null && category.parentCategory !== undefined;
      setCategoryType(hasParent ? 'sub' : 'main');
      setFormData({
        name: category.name,
        description: category.description || '',
        replacementPeriod: category.replacementPeriod,
        returnPeriod: category.returnPeriod,
        parentCategory: typeof category.parentCategory === 'string'
          ? category.parentCategory
          : category.parentCategory?._id || null,
        taxes: Array.isArray(category.taxes)
          ? category.taxes.map(tax => typeof tax === 'string' ? tax : tax._id)
          : []
      });
    } else {
      setEditMode(false);
      setEditId(null);
      setCategoryType('main'); // Default to main category for new categories
      setFormData({
        name: '',
        description: '',
        replacementPeriod: 0,
        returnPeriod: 0,
        parentCategory: null,
        taxes: []
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name) {
      setFormData({ ...formData, [name]: value });
    }
  };
  
  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    if (name) {
      setFormData({ ...formData, [name]: value || null });
    }
  };
  
  const handleMultiSelectChange = (e: SelectChangeEvent<string[]>) => {
    const { name, value } = e.target;
    if (name) {
      // إذا كانت القيمة مصفوفة، استخدمها مباشرة، وإلا فاستخدم مصفوفة فارغة
      const selectedValues = typeof value === 'string' ? value.split(',') : Array.isArray(value) ? value : [];
      console.log(`[MultiSelect] Setting ${name} to:`, selectedValues);
      setFormData(prev => {
        const newFormData = { ...prev, [name]: selectedValues };
        console.log("[MultiSelect] New formData:", newFormData);
        return newFormData;
      });
    }
  };

  const handleCategoryTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value as 'main' | 'sub';
    setCategoryType(value);
    
    // If changing to main category, clear the parent category
    if (value === 'main') {
      setFormData({ ...formData, parentCategory: null });
    }
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name) {
      setFormData({ ...formData, [name]: Number(value) });
    }
  };

  // Add state for field errors
  const [fieldErrors, setFieldErrors] = useState<{
    name?: string;
    description?: string;
    parentCategory?: string;
    replacementPeriod?: string;
    returnPeriod?: string;
    taxes?: string;
  }>({});

  const handleSubmit = async () => {
    // Reset field errors before submission
    setFieldErrors({});
    
    // عمل نسخة جديدة من البيانات مع التأكد من وجود الضرائب
    const dataToSubmit = {
      ...formData,
      taxes: Array.isArray(formData.taxes) ? formData.taxes : []
    };
    
    console.log('===== تقديم بيانات التصنيف =====');
    console.log('formData الأصلي:', formData);
    console.log('هل الضرائب موجودة:', formData.hasOwnProperty('taxes'));
    console.log('نوع الضرائب:', Array.isArray(formData.taxes) ? 'مصفوفة' : typeof formData.taxes);
    console.log('قيمة الضرائب:', formData.taxes);
    console.log('البيانات النهائية للإرسال:', dataToSubmit);
    
    try {
      let result;
      if (editMode && editId) {
        console.log(`تحديث التصنيف رقم: ${editId}`);
        result = await updateCategory(editId, dataToSubmit);
        console.log('نتيجة التحديث:', result);
        setSnackbar({
          open: true,
          message: 'تم تحديث التصنيف بنجاح',
          severity: 'success'
        });
      } else {
        await createCategory(dataToSubmit);
        setSnackbar({
          open: true,
          message: 'تم إنشاء التصنيف بنجاح',
          severity: 'success'
        });
      }
      handleCloseDialog();
      fetchCategories();
    } catch (error: any) {
      console.error('Error saving category:', error);
      
      // Check for enhanced error details from our service layer
      if (error.customMessage || error.field || error.details) {
        // Set specific field error if available
        if (error.field) {
          setFieldErrors({
            [error.field]: error.details || error.customMessage || 'حقل غير صالح'
          });
        }
        
        // Show error message in snackbar
        setSnackbar({
          open: true,
          message: error.customMessage || 'فشل في حفظ التصنيف',
          severity: 'error'
        });
      }
      // Check if it's an Axios error with a response
      else if (error.response) {
        const responseData = error.response.data;
        
        // If the server responded with an error status code
        if (error.response.status === 400) {
          // Check for field-specific validation errors
          if (responseData.field) {
            setFieldErrors({
              [responseData.field]: responseData.details || responseData.msg || 'حقل غير صالح'
            });
          }
          
          // Handle 400 Bad Request (likely duplicate category)
          setSnackbar({
            open: true,
            message: responseData.msg || 'فشل في حفظ التصنيف - بيانات غير صالحة',
            severity: 'error'
          });
        } else if (error.response.status === 401 || error.response.status === 403) {
          // Handle authentication issues
          setSnackbar({
            open: true,
            message: 'غير مصرح لك بإجراء هذه العملية',
            severity: 'error'
          });
        } else if (error.response.status === 503) {
          // Handle database connection issues
          setSnackbar({
            open: true,
            message: 'فشل في حفظ التصنيف - مشكلة في الاتصال بقاعدة البيانات',
            severity: 'error'
          });
        } else {
          // For other status codes
          setSnackbar({
            open: true,
            message: `فشل في حفظ التصنيف - ${responseData?.msg || error.response.statusText || 'خطأ غير معروف'}`,
            severity: 'error'
          });
        }
      } else if (error.request) {
        // Request was made but no response received (network issue)
        setSnackbar({
          open: true,
          message: 'فشل في حفظ التصنيف - مشكلة في الاتصال بالخادم',
          severity: 'error'
        });
      } else {
        // Something else caused the error
        setSnackbar({
          open: true,
          message: 'فشل في حفظ التصنيف - خطأ غير متوقع',
          severity: 'error'
        });
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('هل أنت متأكد من حذف هذا التصنيف؟')) {
      try {
        await deleteCategory(id);
        setSnackbar({
          open: true,
          message: 'تم حذف التصنيف بنجاح',
          severity: 'success'
        });
        fetchCategories();
      } catch (error) {
        console.error('Error deleting category:', error);
        setSnackbar({
          open: true,
          message: 'فشل في حذف التصنيف - قد يكون مرتبطا بمنتجات أو تصنيفات فرعية',
          severity: 'error'
        });
      }
    }
  };

  const getCategoryName = (id: string | undefined) => {
    if (!id) return 'لا يوجد';
    const category = categories.find(cat => cat._id === id);
    return category ? category.name : 'غير معروف';
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Apply filters to categories
  const applyFilters = (categoriesList: Category[]) => {
    return categoriesList.filter(cat => {
      // تصفية حسب اسم التصنيف
      const nameMatch = cat.name.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
                         (cat.description?.toLowerCase() || '').includes(filters.searchTerm.toLowerCase());
      
      // تصفية حسب نوع التصنيف (رئيسي/فرعي)
      const typeMatch = filters.categoryTypeFilter === 'all' ||
                        (filters.categoryTypeFilter === 'main' && !cat.parentCategory) ||
                        (filters.categoryTypeFilter === 'sub' && cat.parentCategory);
      
      // تصفية حسب فترة الاستبدال
      const replacementPeriodMatch = (
        (filters.minReplacementPeriod === '' || cat.replacementPeriod >= parseInt(filters.minReplacementPeriod)) &&
        (filters.maxReplacementPeriod === '' || cat.replacementPeriod <= parseInt(filters.maxReplacementPeriod))
      );
      
      // تصفية حسب فترة الاسترجاع
      const returnPeriodMatch = (
        (filters.minReturnPeriod === '' || cat.returnPeriod >= parseInt(filters.minReturnPeriod)) &&
        (filters.maxReturnPeriod === '' || cat.returnPeriod <= parseInt(filters.maxReturnPeriod))
      );
      
      return nameMatch && typeMatch && replacementPeriodMatch && returnPeriodMatch;
    });
  };

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      searchTerm: '',
      categoryTypeFilter: 'all',
      minReplacementPeriod: '',
      maxReplacementPeriod: '',
      minReturnPeriod: '',
      maxReturnPeriod: ''
    });
  };

  // فلترة التصنيفات الرئيسية (التي ليس لها أب)
  const filteredCategories = applyFilters(categories);
  const mainCategories = filteredCategories.filter(cat => !cat.parentCategory);
  
  // الحصول على التصنيفات الفرعية لتصنيف رئيسي
  const getSubcategories = (parentId: string) => {
    return filteredCategories.filter(cat => {
      if (typeof cat.parentCategory === 'string') {
        return cat.parentCategory === parentId;
      } else if (cat.parentCategory) {
        return cat.parentCategory._id === parentId;
      }
      return false;
    });
  };

  // دالة تكرارية لعرض التصنيفات وتصنيفاتها الفرعية بأي عمق
  // عرض التصنيف مع رقمه المخزن في قاعدة البيانات
  const renderCategoryWithSubcategories = (category: Category, level: number = 0) => {
    const subcategories = getSubcategories(category._id);
    
    return (
      <React.Fragment key={category._id}>
        <TableRow hover sx={{ backgroundColor: level === 0 ? 'inherit' : `rgba(0, 0, 0, ${Math.min(0.02 * level, 0.06)})` }}>
          <TableCell align="center">{category.sequentialId}</TableCell>
          <TableCell sx={{ paddingLeft: level * 4 + 2 }}>
            {level > 0 ? `${'└─'.repeat(1)} ` : ''}{category.name}
          </TableCell>
          <TableCell>{category.description || '-'}</TableCell>
          <TableCell align="center">{category.replacementPeriod} يوم</TableCell>
          <TableCell align="center">{category.returnPeriod} يوم</TableCell>
          <TableCell>
            {category.taxes && Array.isArray(category.taxes) && category.taxes.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {category.taxes.map((tax: any) => {
                  const taxObj = typeof tax === 'string'
                    ? taxes.find(t => t._id === tax)
                    : tax;
                  return (
                    <Box key={typeof tax === 'string' ? tax : tax._id}
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: '16px',
                        padding: '0 6px',
                        fontSize: '0.75rem',
                        bgcolor: 'background.paper'
                      }}
                    >
                      {taxObj ? `${taxObj.name} ${taxObj.rate}%` : ''}
                    </Box>
                  );
                })}
              </Box>
            ) : '-'}
          </TableCell>
          <TableCell>
            {category.parentCategory
              ? getCategoryName(
                  typeof category.parentCategory === 'string'
                    ? category.parentCategory
                    : category.parentCategory?._id
                )
              : '-'}
          </TableCell>
          <TableCell align="center">
            <IconButton onClick={() => handleOpenDialog(category)} color="primary" size="small" title="تعديل">
              <EditIcon />
            </IconButton>
            <IconButton onClick={() => handleDelete(category._id)} color="error" size="small" title="حذف">
              <DeleteIcon />
            </IconButton>
            <IconButton
              onClick={() => navigate(`/category-products/${category._id}`)}
              color="info"
              size="small"
              title="منتجات التصنيف"
            >
              <ViewListIcon />
            </IconButton>
          </TableCell>
        </TableRow>
        
        {/* عرض التصنيفات الفرعية بشكل تكراري */}
        {subcategories.map(subcat => renderCategoryWithSubcategories(subcat, level + 1))}
      </React.Fragment>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          التصنيفات
        </Typography>
        <Box>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setShowFilters(!showFilters)}
            sx={{ mr: 1 }}
          >
            {showFilters ? 'إخفاء الفلتر' : 'عرض الفلتر'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            تصنيف جديد
          </Button>
        </Box>
      </Box>

      {/* قسم الفلتر */}
      {showFilters && (
        <Paper sx={{ p: 2, mb: 3, backgroundColor: 'background.default' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              تصفية التصنيفات
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, flexWrap: 'wrap' }}>
              {/* البحث بالاسم أو الوصف */}
              <TextField
                label="البحث بالاسم أو الوصف"
                variant="outlined"
                size="small"
                value={filters.searchTerm}
                onChange={(e) => setFilters({...filters, searchTerm: e.target.value})}
                sx={{ flexGrow: 1, minWidth: '200px' }}
              />
              
              {/* فلتر نوع التصنيف */}
              <FormControl variant="outlined" size="small" sx={{ minWidth: '180px' }}>
                <InputLabel id="category-type-filter-label">نوع التصنيف</InputLabel>
                <Select
                  labelId="category-type-filter-label"
                  value={filters.categoryTypeFilter}
                  onChange={(e) => setFilters({...filters, categoryTypeFilter: e.target.value as 'all' | 'main' | 'sub'})}
                  label="نوع التصنيف"
                >
                  <MenuItem value="all">الكل</MenuItem>
                  <MenuItem value="main">تصنيفات رئيسية</MenuItem>
                  <MenuItem value="sub">تصنيفات فرعية</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            {/* فلتر فترة الاستبدال */}
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ flexBasis: '100%' }}>
                فترة الاستبدال (بالأيام)
              </Typography>
              <TextField
                label="من"
                variant="outlined"
                size="small"
                type="number"
                value={filters.minReplacementPeriod}
                onChange={(e) => setFilters({...filters, minReplacementPeriod: e.target.value})}
                InputProps={{ inputProps: { min: 0 } }}
                sx={{ width: '120px' }}
              />
              <TextField
                label="إلى"
                variant="outlined"
                size="small"
                type="number"
                value={filters.maxReplacementPeriod}
                onChange={(e) => setFilters({...filters, maxReplacementPeriod: e.target.value})}
                InputProps={{ inputProps: { min: 0 } }}
                sx={{ width: '120px' }}
              />
              
              <Typography variant="body2" sx={{ ml: 3 }}>
                فترة الاسترجاع (بالأيام)
              </Typography>
              <TextField
                label="من"
                variant="outlined"
                size="small"
                type="number"
                value={filters.minReturnPeriod}
                onChange={(e) => setFilters({...filters, minReturnPeriod: e.target.value})}
                InputProps={{ inputProps: { min: 0 } }}
                sx={{ width: '120px' }}
              />
              <TextField
                label="إلى"
                variant="outlined"
                size="small"
                type="number"
                value={filters.maxReturnPeriod}
                onChange={(e) => setFilters({...filters, maxReturnPeriod: e.target.value})}
                InputProps={{ inputProps: { min: 0 } }}
                sx={{ width: '120px' }}
              />
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="text"
                onClick={handleResetFilters}
                sx={{ mr: 1 }}
              >
                مسح الفلتر
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '50px' }} align="center">الرقم</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>اسم التصنيف</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>الوصف</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">فترة الاستبدال</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">فترة الاسترجاع</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>الضرائب</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>التصنيف الرئيسي</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">إجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mainCategories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    {filters.searchTerm || filters.categoryTypeFilter !== 'all' ||
                     filters.minReplacementPeriod || filters.maxReplacementPeriod ||
                     filters.minReturnPeriod || filters.maxReturnPeriod
                      ? 'لا توجد تصنيفات تطابق معايير البحث'
                      : 'لا توجد تصنيفات'}
                  </TableCell>
                </TableRow>
              ) : (
                mainCategories.map(category => renderCategoryWithSubcategories(category))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Category Form Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editMode ? 'تعديل التصنيف' : 'إضافة تصنيف جديد'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              name="name"
              label="اسم التصنيف"
              value={formData.name}
              onChange={handleInputChange}
              required
              fullWidth
              error={!!fieldErrors.name}
              helperText={fieldErrors.name}
            />
            
            <TextField
              name="description"
              label="وصف التصنيف"
              value={formData.description}
              onChange={handleInputChange}
              multiline
              rows={2}
              fullWidth
              error={!!fieldErrors.description}
              helperText={fieldErrors.description}
            />
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                name="replacementPeriod"
                label="فترة الاستبدال (بالأيام)"
                type="number"
                value={formData.replacementPeriod}
                onChange={handleNumberChange}
                fullWidth
                InputProps={{ inputProps: { min: 0 } }}
                error={!!fieldErrors.replacementPeriod}
                helperText={fieldErrors.replacementPeriod}
              />
              
              <TextField
                name="returnPeriod"
                label="فترة الاسترجاع (بالأيام)"
                type="number"
                value={formData.returnPeriod}
                onChange={handleNumberChange}
                fullWidth
                InputProps={{ inputProps: { min: 0 } }}
                error={!!fieldErrors.returnPeriod}
                helperText={fieldErrors.returnPeriod}
              />
            </Box>
            <FormControl component="fieldset" fullWidth sx={{ mb: 1 }}>
              <FormLabel component="legend">نوع التصنيف</FormLabel>
              <RadioGroup
                row
                name="categoryType"
                value={categoryType}
                onChange={handleCategoryTypeChange}
              >
                <FormControlLabel
                  value="main"
                  control={<Radio />}
                  label="تصنيف رئيسي"
                />
                <FormControlLabel
                  value="sub"
                  control={<Radio />}
                  label="تصنيف فرعي"
                />
              </RadioGroup>
            </FormControl>
            
            {/* تصنيف فرعي */}
            {categoryType === 'sub' && (
              <FormControl
                fullWidth
                error={!!fieldErrors.parentCategory}
              >
                <InputLabel id="parent-category-label">التصنيف الرئيسي</InputLabel>
                <Select
                  labelId="parent-category-label"
                  name="parentCategory"
                  value={formData.parentCategory || ''}
                  onChange={handleSelectChange}
                  label="التصنيف الرئيسي"
                  required={categoryType === 'sub'}
                >
                  <MenuItem value="" disabled>
                    <em>اختر التصنيف الرئيسي</em>
                  </MenuItem>
                  {categories
                    .filter(cat => {
                      // استبعاد التصنيف الحالي عند التعديل
                      if (editMode && cat._id === editId) return false;
                      
                      // منع تحديد تصنيف كأب لنفسه أو أي من أبنائه، لتجنب دورات في التسلسل الهرمي
                      if (editMode && editId) {
                        // التحقق مما إذا كان التصنيف الحالي هو أحد أبناء التصنيف الذي يتم تعديله
                        const isDescendant = (potentialParentId: string, categoryId: string): boolean => {
                          // البحث عن التصنيفات الفرعية المباشرة
                          const directChildren = categories.filter(c => {
                            if (typeof c.parentCategory === 'string') {
                              return c.parentCategory === categoryId;
                            } else if (c.parentCategory) {
                              return c.parentCategory._id === categoryId;
                            }
                            return false;
                          });
                          
                          // إذا كان التصنيف الحالي هو ابن مباشر، فلا يمكن استخدامه كأب
                          if (directChildren.some(child => child._id === potentialParentId)) {
                            return true;
                          }
                          
                          // البحث التكراري في المستويات الأعمق
                          return directChildren.some(child => isDescendant(potentialParentId, child._id));
                        };
                        
                        return !isDescendant(cat._id, editId);
                      }
                      
                      return true;
                    })
                    .map(category => (
                      <MenuItem key={category._id} value={category._id}>
                        {category.name}
                        {category.parentCategory ? ` (تحت: ${getCategoryName(
                          typeof category.parentCategory === 'string'
                            ? category.parentCategory
                            : category.parentCategory._id
                        )})` : ''}
                      </MenuItem>
                    ))}
                </Select>
                {categoryType === 'sub' && !formData.parentCategory && (
                  <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                    يجب اختيار تصنيف رئيسي للتصنيف الفرعي
                  </Typography>
                )}
              </FormControl>
            )}
            
            {/* حقل الضرائب */}
            <FormControl fullWidth error={!!fieldErrors.taxes}>
              <InputLabel id="taxes-label">الضرائب</InputLabel>
              <Select
                labelId="taxes-label"
                id="taxes-select"
                name="taxes"
                multiple
                value={formData.taxes || []}
                onChange={handleMultiSelectChange}
                label="الضرائب"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((taxId) => {
                      const tax = taxes.find(t => t._id === taxId);
                      return (
                        <Box key={taxId} sx={{
                          border: '1px solid #e0e0e0',
                          borderRadius: '16px',
                          padding: '0 10px',
                          bgcolor: 'background.paper'
                        }}>
                          {tax ? `${tax.name} (${tax.rate}%)` : taxId}
                        </Box>
                      );
                    })}
                  </Box>
                )}
                sx={{ '& .MuiSelect-select': { display: 'flex', flexWrap: 'wrap', gap: 0.5 } }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 224,
                    },
                  },
                }}
              >
                {taxesLoading ? (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      جاري تحميل الضرائب...
                    </Box>
                  </MenuItem>
                ) : taxes.length === 0 ? (
                  <MenuItem disabled>
                    لا توجد ضرائب متاحة
                  </MenuItem>
                ) : (
                  taxes.filter(tax => tax.isActive).map((tax) => (
                    <MenuItem key={tax._id} value={tax._id}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <Typography>{tax.name}</Typography>
                        <Typography sx={{ color: 'text.secondary' }}>{tax.rate}%</Typography>
                      </Box>
                    </MenuItem>
                  ))
                )}
              </Select>
              <FormHelperText>{fieldErrors.taxes}</FormHelperText>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            إلغاء
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!formData.name || (categoryType === 'sub' && !formData.parentCategory)}
          >
            حفظ
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={5000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CategoryList;